import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Grid,
  Chip,
} from '@mui/material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface ReceptionSummaryPrintProps {
  batchData: {
    batchNumber: string;
    client: any;
    samples: any[];
    selectedElements?: string[];
    analysesDemandees?: string[];
    mechanicalPreparationMethods?: string[];
    priorite?: string;
    dateReception: Date;
    recuPar?: string;
  };
}

const ReceptionSummaryPrint: React.FC<ReceptionSummaryPrintProps> = ({ batchData }) => {
  const methodDescriptions: Record<string, string> = {
    'D4A-ICP- PRE.MO/ANA/038': 'Four Acid Digestion ICP (41 éléments)',
    'D4A-ICP- PRE.MO/ANA/037': 'Four Acid Digestion ICP (23 éléments)',
    'FP-ICP PRE.MO/ANA/007': 'Fusion Peroxide ICP',
    'AA-PRE.MO/ANA/002': 'Aqua Regia AAS',
    'Au-PRE.MO/ANA/001 BLEG ICP': 'Gold BLEG ICP',
    'Au-PRE.MO/ANA/001 TC FG': 'Gold Fire Assay',
    'PM-PRE.MO/ANA/013-01': 'Concassage',
    'PM-PRE.MO/ANA/013-02': 'Broyage',
    'PM-PRE.MO/ANA/013-03': 'Tamisage',
    'BGM-MULTI-LAB': 'BGM Multi-Laboratoire (ICP + AU)',
  };

  const getMethodDescription = (code: string) => {
    return methodDescriptions[code] || code;
  };

  const getPriorityColor = (priority?: string) => {
    switch(priority) {
      case 'URGENTE': return '#d32f2f';
      case 'HAUTE': return '#f57c00';
      case 'NORMALE': return '#388e3c';
      default: return '#757575';
    }
  };

  return (
    <Box
      sx={{
        width: '210mm',
        minHeight: '297mm',
        padding: '10mm',
        backgroundColor: 'white',
        margin: 'auto',
        position: 'relative',
        '@media print': {
          padding: '10mm',
          margin: 0,
          height: 'auto',
          minHeight: 'auto',
        },
        '@media screen': {
          boxShadow: '0 0 10px rgba(0,0,0,0.1)',
          height: 'auto',
        },
      }}
    >
      {/* Simplified Professional Header */}
      <Box sx={{ 
        mb: 2,
        p: 2,
        backgroundColor: '#1976d2',
        color: 'white',
        borderRadius: 1,
        textAlign: 'center',
      }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          ⛏️ AFRILAB MINING SERVICES ⛏️
        </Typography>
        <Typography variant="h5" sx={{ mb: 1 }}>
          FICHE DE RÉCEPTION - Lot {batchData.batchNumber}
        </Typography>
        <Typography variant="h6">
          {format(batchData.dateReception, 'dd MMMM yyyy à HH:mm', { locale: fr })}
        </Typography>
        <Box sx={{ 
          mt: 1,
          p: 1,
          backgroundColor: 'rgba(76, 175, 80, 0.8)',
          borderRadius: 1,
          display: 'inline-block',
        }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            ✅ STATUT: REÇU
          </Typography>
        </Box>
      </Box>

      {/* Client Information - Compact */}
      <Paper sx={{ mb: 2, p: 1.5, backgroundColor: '#f5f5f5' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#1976d2' }}>
          INFORMATIONS CLIENT
        </Typography>
        <Grid container spacing={1} sx={{ fontSize: '0.875rem' }}>
          <Grid item xs={6}>
            <Typography variant="body2"><strong>Société:</strong> {batchData.client?.company_name || 'N/A'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2"><strong>Contact:</strong> {batchData.client?.contact_person || 'N/A'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2"><strong>Email:</strong> {batchData.client?.email || 'N/A'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2"><strong>Téléphone:</strong> {batchData.client?.phone || 'N/A'}</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Analysis Methods - Compact */}
      {(batchData.mechanicalPreparationMethods?.length > 0 || batchData.analysesDemandees?.length > 0) && (
        <Paper sx={{ mb: 2, p: 1.5, backgroundColor: '#f5f5f5' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#4caf50' }}>
            MÉTHODES D'ANALYSE
          </Typography>
          
          {batchData.mechanicalPreparationMethods?.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                Préparation Mécanique:
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                {batchData.mechanicalPreparationMethods.map((method, index) => (
                  <Chip
                    key={index}
                    label={getMethodDescription(method)}
                    size="small"
                    sx={{ 
                      height: '20px',
                      fontSize: '0.7rem',
                      backgroundColor: '#ff9800',
                      color: 'white'
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {batchData.analysesDemandees?.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                Analyses Demandées:
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                {batchData.analysesDemandees.map((method, index) => (
                  <Chip
                    key={index}
                    label={getMethodDescription(method)}
                    size="small"
                    sx={{ 
                      height: '20px',
                      fontSize: '0.7rem',
                      backgroundColor: '#4caf50',
                      color: 'white'
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Paper>
      )}

      {/* Selected Elements - Compact Grid */}
      {batchData.selectedElements?.length > 0 && (
        <Paper sx={{ mb: 2, p: 1.5, backgroundColor: '#f5f5f5' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#9c27b0' }}>
            ÉLÉMENTS SÉLECTIONNÉS ({batchData.selectedElements.length})
          </Typography>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(13, 1fr)',
            gap: 0.3,
            p: 0.5,
            backgroundColor: 'white',
            borderRadius: 1,
            border: '1px solid #e0e0e0',
          }}>
            {batchData.selectedElements.map((element, index) => (
              <Box
                key={index}
                sx={{
                  textAlign: 'center',
                  p: 0.3,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 0.3,
                  border: '1px solid #ddd',
                  fontSize: '0.7rem',
                  fontWeight: 'bold',
                  minWidth: '30px',
                }}
              >
                {element}
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* Samples Table - Compact */}
      <Paper sx={{ mb: 2 }}>
        <Box sx={{ 
          p: 1,
          backgroundColor: '#424242',
          color: 'white',
        }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            ÉCHANTILLONS ({batchData.samples.length})
          </Typography>
        </Box>
        <TableContainer sx={{
          '@media print': {
            overflow: 'visible',
          },
        }}>
          <Table size="small" sx={{
            '@media print': {
              pageBreakInside: 'auto',
            },
          }}>
            <TableHead>
              <TableRow sx={{
                '@media print': {
                  pageBreakInside: 'avoid',
                  pageBreakAfter: 'avoid',
                },
              }}>
                <TableCell sx={{ py: 0.5, fontSize: '0.75rem', fontWeight: 'bold', width: '5%' }}>#</TableCell>
                <TableCell sx={{ py: 0.5, fontSize: '0.75rem', fontWeight: 'bold', width: '30%' }}>Réf. Lab</TableCell>
                <TableCell sx={{ py: 0.5, fontSize: '0.75rem', fontWeight: 'bold', width: '30%' }}>Réf. Client</TableCell>
                <TableCell sx={{ py: 0.5, fontSize: '0.75rem', fontWeight: 'bold', width: '20%' }}>Type</TableCell>
                <TableCell sx={{ py: 0.5, fontSize: '0.75rem', fontWeight: 'bold', width: '15%' }}>Priorité</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{
              '@media print': {
                pageBreakInside: 'auto',
              },
            }}>
              {batchData.samples.map((sample, index) => (
                <TableRow key={sample.id}>
                  <TableCell sx={{ py: 0.5, fontSize: '0.7rem', width: '5%' }}>{index + 1}</TableCell>
                  <TableCell sx={{ 
                    py: 0.5, 
                    fontSize: '0.7rem', 
                    fontWeight: 'bold', 
                    color: '#1976d2',
                    width: '30%',
                    wordBreak: 'break-word',
                  }}>
                    {sample.sample_id}
                  </TableCell>
                  <TableCell sx={{ 
                    py: 0.5, 
                    fontSize: '0.7rem',
                    width: '30%',
                    wordBreak: 'break-word',
                  }}>
                    {sample.external_id || '—'}
                  </TableCell>
                  <TableCell sx={{ py: 0.5, fontSize: '0.7rem', width: '20%' }}>{sample.type_echantillon || 'Roche'}</TableCell>
                  <TableCell sx={{ py: 0.5, width: '15%' }}>
                    <Chip
                      label={sample.priorite || batchData.priorite || 'NORMALE'}
                      size="small"
                      sx={{
                        height: '18px',
                        fontSize: '0.65rem',
                        backgroundColor: getPriorityColor(sample.priorite || batchData.priorite),
                        color: 'white',
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Footer - Compact Signature Section */}
      <Box sx={{ 
        mt: 3,
        pt: 2,
        borderTop: '2px solid #e0e0e0',
        '@media print': {
          pageBreakInside: 'avoid',
        },
      }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Paper sx={{ p: 1.5, backgroundColor: '#f8f9fa' }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>
                RÉCEPTION LABORATOIRE
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {batchData.recuPar || 'Réceptionniste'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {format(batchData.dateReception, 'dd/MM/yyyy HH:mm')}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">Signature:</Typography>
                <Box sx={{ height: 30, borderBottom: '2px solid #1976d2', mt: 0.5 }} />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={6}>
            <Paper sx={{ p: 1.5, backgroundColor: '#f8f9fa' }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>
                VALIDATION CLIENT
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#666' }}>
                Je confirme la réception et l'exactitude des informations
              </Typography>
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary">Nom et Signature:</Typography>
                <Box sx={{ height: 30, borderBottom: '2px solid #4caf50', mt: 0.5 }} />
              </Box>
            </Paper>
          </Grid>
        </Grid>
        
        {/* Simplified Footer */}
        <Box sx={{ 
          mt: 2, 
          p: 1.5, 
          backgroundColor: '#e3f2fd',
          borderRadius: 1,
          textAlign: 'center',
          border: '1px solid #1976d2',
        }}>
          <Typography variant="body2" sx={{ 
            color: '#1976d2', 
            fontWeight: 'bold',
            mb: 0.5,
          }}>
            🏭 AFRILAB MINING SERVICES - LABORATOIRE D'ANALYSES MINIÈRES
          </Typography>
          <Typography variant="caption" sx={{ 
            color: '#666', 
            fontSize: '0.8rem',
            display: 'block',
          }}>
            Document généré par AFRILAB LIMS v2.0 - Système de Traçabilité Complète
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ReceptionSummaryPrint;